import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Trophy, Star, TrendingUp, Clock, Target, MessageSquare, Download, Share2, RefreshCw } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/DashboardLayout";

interface SessionResult {
  id: string;
  interview_type: string;
  overall_score: number;
  duration: number;
  completed_at: string;
  answers: Array<{
    question_text: string;
    answer_text: string;
    overall_score: number;
    content_score: number;
    structure_score: number;
    communication_score: number;
    relevance_score: number;
    duration: number;
    feedback: any;
  }>;
}

const InterviewResults = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [results, setResults] = useState<SessionResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResults();
  }, [sessionId]);

  const loadResults = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Load session
      const { data: session, error: sessionError } = await supabase
        .from('interview_sessions')
        .select('*')
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .single();

      if (sessionError) throw sessionError;

      // Load answers with questions
      const { data: answers, error: answersError } = await supabase
        .from('interview_answers')
        .select(`
          *,
          interview_questions (
            text
          )
        `)
        .eq('session_id', sessionId)
        .order('created_at');

      if (answersError) throw answersError;

      const formattedAnswers = answers.map((answer: any) => ({
        question_text: answer.interview_questions.text,
        answer_text: answer.answer_text,
        overall_score: answer.overall_score || 0,
        content_score: answer.content_score || 0,
        structure_score: answer.structure_score || 0,
        communication_score: answer.communication_score || 0,
        relevance_score: answer.relevance_score || 0,
        duration: answer.duration,
        feedback: answer.feedback
      }));

      setResults({
        ...session,
        answers: formattedAnswers
      });
    } catch (error) {
      console.error('Error loading results:', error);
      toast({
        title: "Error",
        description: "Failed to load interview results",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return "text-green-500";
    if (score >= 6) return "text-yellow-500";
    return "text-red-500";
  };

  const getGrade = (score: number) => {
    if (score >= 9) return "A+";
    if (score >= 8.5) return "A";
    if (score >= 8) return "A-";
    if (score >= 7.5) return "B+";
    if (score >= 7) return "B";
    if (score >= 6.5) return "B-";
    if (score >= 6) return "C+";
    if (score >= 5.5) return "C";
    return "D";
  };

  if (loading || !results) {
    return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading results...</p>
          </div>
        </div>
    );
  }

  const avgScore = results.answers.length > 0 
    ? results.answers.reduce((sum, a) => sum + a.overall_score, 0) / results.answers.length 
    : 0;

  return (
    <>
      <Helmet>
        <title>Interview Results | AI Interview Prep</title>
      </Helmet>

      
        <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
          <div className="max-w-7xl mx-auto px-6 py-12 space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold">Interview Complete! 🎉</h1>
              <p className="text-muted-foreground text-lg">
                Great work! Here's your detailed performance report.
              </p>
            </div>

            {/* Overall Score Card */}
            <Card className="border-2 border-primary/20">
              <CardHeader>
                <CardTitle className="text-center text-2xl">Overall Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center space-y-2">
                  <div className={`text-6xl font-bold ${getScoreColor(avgScore)}`}>
                    {avgScore.toFixed(1)}/10
                  </div>
                  <Badge variant="secondary" className="text-lg px-4 py-1">
                    Grade: {getGrade(avgScore)}
                  </Badge>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-card/50">
                    <CardContent className="p-4 text-center">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 text-primary" />
                      <div className="text-2xl font-bold">{results.answers.length}</div>
                      <div className="text-xs text-muted-foreground">Questions Answered</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card/50">
                    <CardContent className="p-4 text-center">
                      <Clock className="w-8 h-8 mx-auto mb-2 text-accent" />
                      <div className="text-2xl font-bold">{formatDuration(results.duration || 0)}</div>
                      <div className="text-xs text-muted-foreground">Total Time</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card/50">
                    <CardContent className="p-4 text-center">
                      <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                      <div className="text-2xl font-bold">
                        {results.answers.filter(a => a.overall_score >= 8).length}
                      </div>
                      <div className="text-xs text-muted-foreground">High Scores (8+)</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card/50">
                    <CardContent className="p-4 text-center">
                      <Trophy className="w-8 h-8 mx-auto mb-2 text-primary" />
                      <div className="text-2xl font-bold">
                        {Math.round((results.answers.filter(a => a.overall_score >= 7).length / results.answers.length) * 100)}%
                      </div>
                      <div className="text-xs text-muted-foreground">Pass Rate</div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Score Breakdown</CardTitle>
                <CardDescription>Performance across different evaluation criteria</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: "Content Quality", score: results.answers.reduce((sum, a) => sum + a.content_score, 0) / results.answers.length },
                  { label: "Structure & Clarity", score: results.answers.reduce((sum, a) => sum + a.structure_score, 0) / results.answers.length },
                  { label: "Communication Style", score: results.answers.reduce((sum, a) => sum + a.communication_score, 0) / results.answers.length },
                  { label: "Relevance", score: results.answers.reduce((sum, a) => sum + a.relevance_score, 0) / results.answers.length }
                ].map((item) => (
                  <div key={item.label} className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium">{item.label}</span>
                      <span className={`font-bold ${getScoreColor(item.score)}`}>
                        {item.score.toFixed(1)}/10
                      </span>
                    </div>
                    <Progress value={item.score * 10} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Question-by-Question Results */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Question-by-Question Review</h2>
              {results.answers.map((answer, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg">Question {index + 1}</CardTitle>
                        <CardDescription className="mt-2">{answer.question_text}</CardDescription>
                      </div>
                      <Badge className={getScoreColor(answer.overall_score)}>
                        {answer.overall_score.toFixed(1)}/10
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Your Answer:</p>
                      <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded">
                        {answer.answer_text}
                      </p>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {formatDuration(answer.duration)}
                      </span>
                      <span className="text-muted-foreground">
                        {answer.answer_text.length} characters
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => navigate('/app/ai-interview-prep/practice/quick')}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Practice Again
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/app/ai-interview-prep')}>
                Back to AI Interview Prep
              </Button>
              <Button size="lg" variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Download Report
              </Button>
            </div>
          </div>
        </div>
      
    </>
  );
};

export default InterviewResults;
